import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next'; // [!code ++]

import { Button, DateRangePicker, DescriptionBar, Grid, MenuTab, SearchBar, SelectBox, Modal, ModalInner, Input, Text } from 'components';
import { scheduleGridColumns, cancelStatus as cancelStatusMap, status as statusMap, statusList } from './utils';

import axios from 'common/AxiosAuth';
import { getScheduleBuffer, createScheduleBuffer, updateScheduleBuffer, deleteScheduleBuffer } from 'api/scheduleBuffer';
import dayjs from 'dayjs';
 
import { ScheduleItem } from './type';
// import { cloneDeep } from 'lodash-es';

// 로컬 타입 정의

const MENU_INDEX = {
  RESERVED_SCHEDULE: 0,
  DUPLICATE_BUFFER: 1
};

const ManageSchedule = () => {
  const { t } = useTranslation(); // [!code ++]
  const [selectedMenuIndex, setSelectedMenuIndex] = useState(MENU_INDEX.RESERVED_SCHEDULE);
  const [searchValue, setSearchValue] = useState<string>('');

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scheduleData, setScheduleData] = useState<ScheduleItem[]>([]);
  const [totalPages, setTotalPages] = useState<number>(1);

  // 필터 상태
  const [groundStationName, setGroundStationName] = useState<string>('');
  const [dateRange, setDateRange] = useState<string[]>([]);
  const [sortBy] = useState<string>('scheduleId');
  const [sortDirection] = useState<'asc' | 'desc'>('desc');

  // SelectBox 옵션들
  const [selectGroundstationList, setSelectGroundstationList] = useState<any[]>([]);

  const [editingStatusRowId, setEditingStatusRowId] = useState<number | null>(null);
  const [statusEditValue, setStatusEditValue] = useState<string>('');

  const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false);
  const [cancelData, setCancelData] = useState<ScheduleItem | null>(null);
  

  // Duplicate Buffer 상태
  const [bufferId, setBufferId] = useState<number | null>(null);
  const [bufferMinutes, setBufferMinutes] = useState<number | string>('');
  const [bufferUpdatedAt, setBufferUpdatedAt] = useState<string | null>(null);
  const [isBufferLoading, setIsBufferLoading] = useState<boolean>(false);

  // number value를 status string으로 변환하는 매핑 (백엔드 Enum 순서에 맞게 수정)
  const statusValueToStatus = (value: number): string => {
    const statusMap: Record<number, string> = {
      1: 'IDLE',
      2: 'SCHEDULED',
      3: 'PROCESS',
      4: 'COMPLETE',
      5: 'FAIL',
      6: 'CANCEL'
    };
    return statusMap[value] || 'IDLE';
  };

  // status string을 number value로 변환하는 매핑 (백엔드 Enum 순서에 맞게 수정)
  const statusToStatusValue = (status: string): number => {
    const valueMap: Record<string, number> = {
      'IDLE': 1,
      'SCHEDULED': 2,
      'PROCESS': 3,
      'COMPLETE': 4,
      'FAIL': 5,
      'CANCEL': 6,
      // 기존 값들과의 호환성 유지
      'IN_PROGRESS': 3,
      'COMPLETED': 4,
      'CANCELLED': 6,
      'FAILED': 5
    };
    return valueMap[status] || 1;
  };

  useEffect(() => {
    if (searchValue.trim() === '') {
      getSchedule();
    } else {
      searchSchedule(searchValue, currentPage);
    }
    fetchGroundStationOptions();
  }, [currentPage, sortBy, sortDirection, searchValue]);

  // 검색어가 변경될 때 currentPage를 1로 리셋
  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue]);

  const getSchedule = async () => {
    try {
      const params = new URLSearchParams();

      if (groundStationName) params.append('groundStationName', groundStationName);
      if (dateRange.length === 2) {
        params.append('startDate', dateRange[0]);
        params.append('endDate', dateRange[1]);
      }

      if (searchValue) params.append('keyword', searchValue);
      params.append('page', (currentPage - 1).toString());
      params.append('size', '10');
      params.append('sort', `${sortBy},${sortDirection}`);

      const { data } = await axios.get(`/api/admin/schedule/search?${params.toString()}`);
      console.log('schedule data', data);

      // 페이징 정보 설정
      setTotalPages(data.totalPages);

      const scheduleGridData = data.content.map((item: ScheduleItem) => {
        return {
          scheduleId: item.scheduleId,
          orderId: item.orderId,
          memberId: item.memberId,
          email: item.email,
          tleTitle: item.tleTitle,
          groundStationName: item.groundStationName,
          startTime: item.startTime,
          endTime: item.endTime,
          reservationDate: item.reservationDate,
          status: item.status,
          cancelStatus: item.cancelStatus,
          cancelable: item.cancelable,
        };
      });
      setScheduleData(scheduleGridData);
    } catch (error) {
      console.error('스케줄 조회 실패:', error);
      // 에러 시 빈 배열 사용
      setScheduleData([]);
      setTotalPages(1);
    }
  };

  // 검색어가 있을 때만 호출되는 검색 함수
  const searchSchedule = async (query: string, pageNum: number) => {
    try {
      const params = new URLSearchParams();
      
      if (groundStationName) params.append('groundStationName', groundStationName);
      if (dateRange.length === 2) {
        params.append('startDate', dateRange[0]);
        params.append('endDate', dateRange[1]);
      }
      
      params.append('keyword', query);
      params.append('page', (pageNum - 1).toString());
      params.append('size', '10');
      params.append('sort', `${sortBy},${sortDirection}`);

      const { data } = await axios.get(`/api/admin/schedule/search?${params.toString()}`);
      console.log('search schedule data', data);

      // 페이징 정보 설정
      setTotalPages(data.totalPages);

      const scheduleGridData = data.content.map((item: ScheduleItem) => {
        return {
          scheduleId: item.scheduleId,
          orderId: item.orderId,
          memberId: item.memberId,
          email: item.email,
          tleTitle: item.tleTitle,
          groundStationName: item.groundStationName,
          startTime: item.startTime,
          endTime: item.endTime,
          reservationDate: item.reservationDate,
          status: item.status,
          cancelStatus: item.cancelStatus,
          cancelable: item.cancelable,
        };
      });
      setScheduleData(scheduleGridData);
    } catch (error) {
      console.error('검색 실패:', error);
    }
  };

  // 지상국 옵션 조회
  const fetchGroundStationOptions = async () => {
    try {
      const response = await axios.get('/api/groundstation/listAll');
      const options = response.data.map((item: any) => ({
        value: item.name, // ID 대신 이름을 value로 사용
        label: item.name
      }));
      setSelectGroundstationList(options);
    } catch (error) {
      console.error('지상국 목록 조회 실패:', error);
    }
  };

  // 스케줄 상태 변경 API 호출 (orderId 포함)
  const updateScheduleStatus = async (scheduleId: number, orderId: string, status: string) => {
    try {
      const requestData = {
        id: scheduleId, // string에서 number로 변경
        orderId: orderId,
        status: status
      };
      
      console.log('API 요청 URL:', '/api/admin/schedule/status');
      console.log('API 요청 데이터:', requestData);
      console.log('scheduleId 타입:', typeof scheduleId);
      
      await axios.patch('/api/admin/schedule/status', requestData);
      console.log('스케줄 상태 변경 성공:', { scheduleId, orderId, status });
      // 성공 시 데이터 다시 조회
      getSchedule();
    } catch (error) {
      console.error('스케줄 상태 변경 실패:', error);
      console.error('요청 데이터:', { id: scheduleId, orderId, status });
      console.error('에러 응답:', (error as any).response?.data);
      alert('상태 변경에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // function onClickDeleteInDetailModal() {
  //   const data = cloneDeep(modalData);
  //   setModalData(null);
  // }

  // async function onClickDetail(data: ScheduleItem) {
  //   console.log('스케줄 상세:', data);
  //   // TODO: 스케줄 상세 API 호출
  //   setModalData(data);
  // }

  function onClickTab(index: number) {
    setSelectedMenuIndex(index);
  }

  // 검색 핸들러
  const handleSearch = () => {
    setCurrentPage(1);
    getSchedule();
  };

  // 기본 스케줄 조회 (필터 없이)
  const getBasicSchedule = async () => {
    try {
      const params = new URLSearchParams();
      
      // 필터 없이 기본 조회
      params.append('page', (currentPage - 1).toString());
      params.append('size', '10');
      params.append('sort', `${sortBy},${sortDirection}`);

      const { data } = await axios.get(`/api/admin/schedule/search?${params.toString()}`);
      console.log('basic schedule data', data);

      // 페이징 정보 설정
      setTotalPages(data.totalPages);

      const scheduleGridData = data.content.map((item: ScheduleItem) => {
        return {
          scheduleId: item.scheduleId,
          orderId: item.orderId,
          memberId: item.memberId,
          email: item.email,
          tleTitle: item.tleTitle,
          groundStationName: item.groundStationName,
          startTime: item.startTime,
          endTime: item.endTime,
          reservationDate: item.reservationDate,
          status: item.status,
          cancelStatus: item.cancelStatus,
          cancelable: item.cancelable,
        };
      });
      setScheduleData(scheduleGridData);
    } catch (error) {
      console.error('기본 스케줄 조회 실패:', error);
      // 에러 시 빈 배열 사용
      setScheduleData([]);
      setTotalPages(1);
    }
  };

  // 리셋 핸들러
  const handleReset = () => {
    setGroundStationName('');
    setDateRange([]);
    setSearchValue('');
    setCurrentPage(1);
    // 모든 필터가 리셋된 후 기본 조회
    setTimeout(() => {
      getBasicSchedule();
    }, 0);
  };

  // 정렬 핸들러
  // const handleSortChange = (newSortBy: string, newSortDirection: 'asc' | 'desc') => {
  //   setSortBy(newSortBy);
  //   setSortDirection(newSortDirection);
  //   getSchedule();
  // };

  // Cancel 버튼 클릭 핸들러
  const onClickCancel = (schedule: ScheduleItem) => {
    setCancelData(schedule);
    setIsDeleteModalOpened(true);
  };

  // Cancel 확인 핸들러
  const handleCancelConfirmed = async () => {
    if (!cancelData) return;
    
    try {
      // 관리자 전용 Cancel API 호출
      await axios.post('/api/admin/schedule/cancel', {
        orderId: cancelData.orderId,
        cancelReason: '관리자 취소'
      });
      
      console.log('스케줄 취소 성공:', cancelData);
      setIsDeleteModalOpened(false);
      setCancelData(null);
      // 데이터 재조회
      getSchedule();
    } catch (error) {
      console.error('스케줄 취소 실패:', error);
      alert('취소에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // Cancel 모달 닫기
  const handleCancelModalClose = () => {
    setIsDeleteModalOpened(false);
    setCancelData(null);
  };

  // * Manage User에서 자동 검색 이동 
  useEffect(() => {
    const autoSearch = sessionStorage.getItem('scheduleAutoSearch');
    const searchKeyword = sessionStorage.getItem('scheduleSearchKeyword');
    
    if (autoSearch === 'true' && searchKeyword) {
      setSearchValue(searchKeyword);
      searchSchedule(searchKeyword, 1); // * 검색 API 호출 
      sessionStorage.removeItem('scheduleAutoSearch');
      sessionStorage.removeItem('scheduleSearchKeyword');
    }
  }, []);

  // Duplicate Buffer 조회 (탭 전환 시)
  useEffect(() => {
    if (selectedMenuIndex === MENU_INDEX.DUPLICATE_BUFFER) {
      fetchScheduleBuffer();
    }
  }, [selectedMenuIndex]);

  const fetchScheduleBuffer = async () => {
    try {
      setIsBufferLoading(true);
      const data = await getScheduleBuffer();
      setBufferId(data?.id ?? null);
      setBufferMinutes(data?.bufferMinutes ?? '');
      setBufferUpdatedAt(data?.updatedAt ?? null);
    } catch (e) {
      // 설정이 없을 수 있으니 초기화만
      setBufferId(null);
      setBufferMinutes('');
      setBufferUpdatedAt(null);
    } finally {
      setIsBufferLoading(false);
    }
  };

  const handleSaveBuffer = async () => {
    const minutesNumber = typeof bufferMinutes === 'string' ? Number(bufferMinutes) : bufferMinutes;
    if (Number.isNaN(minutesNumber) || minutesNumber === null || minutesNumber === undefined) {
      alert('Buffer minutes 값을 입력해주세요.');
      return;
    }
    if (minutesNumber < 0) {
      alert('0 이상으로 입력해주세요.');
      return;
    }

    try {
      if (bufferId) {
        const res = await updateScheduleBuffer({ bufferMinutes: minutesNumber });
        setBufferId(res.id);
        setBufferMinutes(res.bufferMinutes);
        setBufferUpdatedAt(res.updatedAt ?? null);
        await fetchScheduleBuffer();
      } else {
        const res = await createScheduleBuffer({ bufferMinutes: minutesNumber });
        setBufferId(res.id);
        setBufferMinutes(res.bufferMinutes);
        setBufferUpdatedAt(res.updatedAt ?? null);
        await fetchScheduleBuffer();
      }
    } catch (e: any) {
      console.error('버퍼 저장 실패:', e);
    }
  };

  const [isBufferDeleteModalOpened, setIsBufferDeleteModalOpened] = useState(false);

  const openBufferDeleteModal = () => {
    setIsBufferDeleteModalOpened(true);
  };

  const closeBufferDeleteModal = () => {
    setIsBufferDeleteModalOpened(false);
  };

  const handleDeleteBuffer = async () => {
    if (!bufferId) return;
    try {
      const ok = await deleteScheduleBuffer(bufferId);
      if (ok) {
        setBufferId(null);
        setBufferMinutes('');
        setBufferUpdatedAt(null);
      } else {
        console.error('삭제 실패 처리: 응답이 성공 형태가 아님');
      }
    } catch (e: any) {
      console.error('버퍼 삭제 실패:', e);
      const status = e?.response?.status;
      const message: string | undefined = e?.response?.data?.message || e?.response?.data;
      if (status === 400 && message && message.includes('존재하지 않습니다')) {
        // 서버 기준: 이미 없음 → 프론트 상태를 초기화하고 안내
        setBufferId(null);
        setBufferMinutes('');
        setBufferUpdatedAt(null);
      } else if (status === 401) {
        // 인증 만료 시에도 메시지 없이 처리
      } else {
        // 기타 오류도 메시지 없이 처리
      }
    } finally {
      setIsBufferDeleteModalOpened(false);
    }
  };

  // Duplicate Buffer 미니 타임라인 표시용 계산값
  const bufferNum = Number(bufferMinutes || 0) || 0;
  const baseTime = dayjs().hour(10).minute(0).second(0);
  const leftTime = baseTime.subtract(bufferNum, 'minute').format('HH:mm');
  const centerTime = baseTime.format('HH:mm');
  const rightTime = baseTime.add(bufferNum, 'minute').format('HH:mm');
  const formattedUpdatedAt = bufferUpdatedAt ? dayjs(bufferUpdatedAt).format('YYYY-MM-DD HH:mm:ss') : null;

  return (
    <div className="flex h-full w-full flex-col">
      <DescriptionBar
        className="md:px-6"
        title={t('manageSchedule.pageTitle')}
        description={t('manageSchedule.description')}
      />
      <div className="w-full flex-1 flex flex-col">
        <MenuTab
          menuList={[t('manageSchedule.tabs.reservedSchedule'), t('manageSchedule.tabs.duplicateBuffer')]}
          selectedMenuIndex={selectedMenuIndex}
          onClickTab={onClickTab}
        />
        <div className="flex-1 flex flex-col">
          {selectedMenuIndex === MENU_INDEX.RESERVED_SCHEDULE && (
            <div className="flex-1 flex flex-col">
              {/* 필터 영역 */}
              <div className="gap-5 p-4 md:p-6 grid grid-cols-2 grid-rows-2 md:grid-cols-4 md:grid-rows-1 grid-rows-md">

              <SelectBox
                className="max-w-none"
                bgColor="layer2"
                label="Ground Station"
                labelTooltip=""
                list={selectGroundstationList}
                  value={groundStationName}
                  setValue={setGroundStationName}
              />
              <DateRangePicker
                className="col-span-2"
                bgColor="layer2"
                  label="Date Range"
                  labelTooltip="Date Range"
                  value={dateRange}
                  setValue={setDateRange}
              />
                <div className="flex items-end justify-start gap-2">
              <SearchBar
                label="Search"
                    className="flex-1"
                value={searchValue}
                setValue={setSearchValue}
                placeholder="Search"
              />
                  <Button
                    className="h-8"
                    type="blue"
                    text="Search"
                    onClick={handleSearch}
                  />
                  <Button
                    className="h-8"
                    type="secondary"
                    text="Reset"
                    onClick={handleReset}
                  />
                </div>
              </div>

              {/* 그리드 영역 */}
              <div className="flex-1 px-6">
                <Grid
                  gridColumns={scheduleGridColumns}
                  gridData={scheduleData}
                  searchValue={searchValue}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  options={{
                    useFilter: true,
                    useSearch: {
                      use: true,
                      searchKeys: ['scheduleId', 'memberId', 'email', 'tleTitle', 'groundStationName', 'startTime', 'endTime', 'reservationDate', 'status', 'cancelStatus']
                    },
                    useCheckbox: false,
                    useSort: {
                      use: true,
                      sortKeys: ['scheduleId', 'memberId', 'email', 'tleTitle', 'groundStationName', 'startTime', 'endTime', 'reservationDate', 'status', 'cancelStatus', 'cancelable']
                    },
                    usePagination: {
                      use: true,
                      maxPage: totalPages
                    },
                    customCell: {
                      status: (value, row) => {
                        const schedule = row as ScheduleItem;
                        
                        // 이미 CANCEL + REFUNDED인 경우 드롭다운 비활성화 (클릭 불가)
                        const isLocked = schedule.status === 'CANCEL' && schedule.cancelStatus === 'REFUNDED';
                        
                        if (editingStatusRowId === row.scheduleId && !isLocked) {
                          return (
                            <SelectBox
                              className="min-w-[120px] z-[9999]"
                              list={statusList as any}
                              value={statusToStatusValue(statusEditValue)}
                              setValue={(v) => setStatusEditValue(statusValueToStatus(v))}
                              label=""
                              onChange={(newValue) => {
                                const statusString = statusValueToStatus(newValue);
                                setStatusEditValue(statusString);
                                
                                const { orderId } = row;
                                if (!orderId) {
                                  alert('orderId가 없습니다. 다시 시도해주세요.');
                                  return;
                                }
                                
                                console.log('상태 변경 시 row 데이터:', row);
                                console.log('사용할 scheduleId:', row.scheduleId);
                                console.log('사용할 orderId:', orderId);
                                console.log('변경할 status:', statusString);
                                
                                updateScheduleStatus(row.scheduleId, orderId, statusString);
                                
                                setEditingStatusRowId(null);
                              }}
                            />
                          );
                        }
                        
                        const s = statusMap[value] || statusMap['UNKNOWN'];
                        return (
                          <div
                            title={isLocked ? '환불 완료된 스케줄은 상태를 변경할 수 없습니다.' : value}
                            onClick={() => {
                              // CANCEL + REFUNDED면 클릭 무시
                              if (isLocked) return;
                              
                              setEditingStatusRowId(null);
                              setStatusEditValue('');
                              
                              setTimeout(() => {
                                setEditingStatusRowId(row.scheduleId);
                                setStatusEditValue(value);
                              }, 0);
                            }}
                            style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}
                          >
                            <div className={`flex h-6 w-fit items-center rounded-full border px-3 ${s.className} ${isLocked ? 'opacity-50' : ''}`}>
                              <span className="text-nowrap text-small font-regular">{s.title}</span>
                            </div>
                          </div>
                        );
                      },
                      cancel: (value, row) => {
                        const schedule = row as ScheduleItem;
                        if (schedule.cancelable) {
                          return (
                            <button
                              type="button"
                              className="cursor-pointer flex flex-row gap-1 items-center rounded border px-2 h-6 text-xs font-regular bg-white text-black border-border_primary hover:bg-blue-50"
                              onClick={() => onClickCancel(schedule)}
                            >
                              <span className="select-none text-nowrap">Cancel</span>
                            </button>
                          );
                        } else {
                          return (
                            <button
                              type="button"
                              className="cursor-not-allowed flex flex-row gap-1 items-center rounded border px-2 h-6 text-xs font-regular bg-gray-100 text-gray-400 border-border_primary opacity-50"
                              disabled
                            >
                              <span className="select-none text-nowrap">Cancel</span>
                            </button>
                          );
                        }
                      },
                      cancelStatus: (value) => {
                        const status = cancelStatusMap[value] || cancelStatusMap['NONE'];
                        return (
                          <div title={value}>
                            <div className={`flex h-6 w-fit items-center rounded-full border px-3 ${status.className}`}>
                              <span className="text-nowrap text-small font-regular">{status.title}</span>
                            </div>
                          </div>
                        );
                      }
                    }
                  }}

                />
              </div>
            </div>
          )}

          {selectedMenuIndex === MENU_INDEX.DUPLICATE_BUFFER && (
            <div className="h-full justify-items-center py-5">
              {/* Left: Visual timeline card */}
              <div className="border min-w-[500px] mx-auto border-border-dimmed rounded-lg p-4 bg-white shadow-sm">
                <Text className="text-center mb-3" size="body1" weight="medium">Setting Duplicate Buffer</Text>
                <div className="rounded-xl bg-gray-50 p-4">
                  <div className="flex items-center justify-center gap-2">
                    <div className="flex items-center justify-center h-16 px-3 rounded-md bg-emerald-100 text-emerald-700 text-sm font-medium min-w-[84px]">
                      -{bufferNum} min
                    </div>
                    <div className="flex items-center justify-center h-16 px-6 rounded-md bg-slate-800 text-white text-base font-medium min-w-[160px]">
                      Scheduled
                    </div>
                    <div className="flex items-center justify-center h-16 px-3 rounded-md bg-emerald-100 text-emerald-700 text-sm font-medium min-w-[84px]">
                      +{bufferNum} min
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="relative h-8">
                      <div className="absolute left-0 right-0 top-3 h-[2px] bg-gray-300" />
                      <div className="absolute left-[10%] top-[6px] w-[2px] h-3 bg-gray-400" />
                      <div className="absolute left-1/2 -translate-x-1/2 top-[2px] w-[2px] h-5 bg-gray-600" />
                      <div className="absolute right-[10%] top-[6px] w-[2px] h-3 bg-gray-400" />
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-600 px-1">
                      <span>{leftTime}</span>
                      <span className="font-medium text-gray-800">{centerTime}</span>
                      <span>{rightTime}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-4 py-3">
                  <div className='flex gap-3 max-w-[380px] items-end'>
                    <Input
                      type="number"
                      label="Duplicate Buffer (min)"
                      labelTooltip="예약 사이 최소 간격(분)으로, 설정한 값만큼 앞뒤로 여유 시간을 둡니다."
                      value={bufferMinutes}
                      setValue={setBufferMinutes}
                      placeholder="예: 5"
                    />
                      <Button
                        className="h-8"
                        type="blue"
                        text={bufferId ? 'Update' : 'Create'}
                        onClick={handleSaveBuffer}
                        disabled={isBufferLoading}
                      />
                      {bufferId && (
                        <Button
                          className="h-8"
                          type="secondary"
                          text="Delete"
                          onClick={openBufferDeleteModal}
                          disabled={isBufferLoading}
                        />
                      )}
                  </div>
                {isBufferLoading ? (
                  <div className="text-sm text-gray-500">불러오는 중...</div>
                ) : bufferId ? (
                  <div className="flex items-center gap-3 rounded-lg border border-border-dimmed bg-gray-50 p-3">
                    <span className="text-sm text-gray-600">현재 버퍼</span>
                    <span className="inline-flex items-center rounded-full bg-slate-800 px-3 py-1 text-sm font-medium text-white">
                      {bufferMinutes}분
                    </span>
                    <span className="ml-auto text-sm text-gray-500">
                      업데이트 시간: {formattedUpdatedAt || '-'}
                    </span>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">현재 설정된 버퍼가 없습니다.</div>
                )}
              </div>
              </div>

            </div>
          )}
          {isBufferDeleteModalOpened && (
            <Modal isOpened={true}>
              <ModalInner className="md:w-[400px]" title="Delete Buffer" onClickClose={closeBufferDeleteModal}>
                <p className="p-4 pt-0 text-body1">
                  버퍼 설정을 삭제하시겠습니까?<br />이 작업은 되돌릴 수 없습니다.
                </p>
                <div className="flex w-full flex-row items-center justify-between bg-bg-transparent_gray_light p-4">
                  <Button className="h-9" text="Close" onClick={closeBufferDeleteModal} type="secondary" size="large" />
                  <Button className="h-9 bg-red-600 border-red-600" text="Delete" onClick={handleDeleteBuffer} size="large" />
                </div>
              </ModalInner>
            </Modal>
          )}
        </div>
      </div>
      {/* Cancel 확인 모달 */}
      {isDeleteModalOpened && cancelData && (
        <Modal isOpened={true}>
          <ModalInner className="md:w-[400px]" title="Cancel Schedule" onClickClose={handleCancelModalClose}>
            <p className="p-4 pt-0 text-body1">
              Are you sure you want to cancel your schedule?<br />This operation cannot be undone.
            </p>
            <div className="flex w-full flex-row items-center justify-between bg-bg-transparent_gray_light p-4">
              <Button className="h-9" text="Close" onClick={handleCancelModalClose} type="secondary" size="large" />
              <Button className="h-9 bg-red-600 border-red-600" text="Cancel" onClick={handleCancelConfirmed} size="large" />
            </div>
          </ModalInner>
        </Modal>
      )}
    </div>
  );
};

export default ManageSchedule;
